// ─── Amoy RPC resolution ──────────────────────────────

/**
 * Public Amoy endpoint used when VITE_POLYGON_RPC_URL is unset or refused.
 * The old default, rpc-amoy.polygon.technology, no longer resolves in public
 * DNS, so every Testnet read through it failed.
 */
export const AMOY_FALLBACK_RPC_URL = "https://polygon-amoy-bor-rpc.publicnode.com";

// Hosts that answer as Polygon mainnet (chain 137). The check is narrow on
// purpose: it never rejects a URL just because it lacks the word "amoy".
// Note: publicnode uses "polygon-bor-rpc" for mainnet vs "polygon-amoy-bor-rpc"
// for Amoy, so the pattern below only matches the mainnet variant.
const MAINNET_HOST_PATTERNS = [
  /polygon-mainnet/i,
  /^polygon-rpc\.com$/i,
  /^polygon-bor-rpc\.publicnode\.com$/i,
];

/**
 * VITE_POLYGON_RPC_URL is the AMOY endpoint, read by both the chain client
 * (config/contracts.ts) and Privy's embedded wallet (config/privy.ts). If it
 * points at mainnet, every Testnet contract call finds no code at the address
 * and comes back as `0x`, which ethers reports as BAD_DATA. Prod had exactly
 * this: on-chain registration and balances failed, and test-USDT mints hung.
 * Refuse such a URL so callers fall back to a real Amoy endpoint.
 */
export function resolveAmoyRpcUrl(raw: string | undefined): string | undefined {
  const value = raw?.trim();
  if (!value) return undefined;

  let host: string;
  try {
    host = new URL(value).hostname;
  } catch {
    console.warn("[rpcUrls] VITE_POLYGON_RPC_URL is not a valid URL; using the default Amoy RPC.");
    return undefined;
  }

  if (MAINNET_HOST_PATTERNS.some((pattern) => pattern.test(host))) {
    // Log the host only -- the path carries the provider API key.
    console.warn(
      `[rpcUrls] VITE_POLYGON_RPC_URL points at a Polygon MAINNET host (${host}) but is the Amoy/Testnet RPC; using the default Amoy RPC instead. Fix the env var.`,
    );
    return undefined;
  }
  return value;
}
